import React, { useEffect } from 'react';
import {
    Autocomplete, Box, Button, Typography, Select, MenuItem, InputLabel, FormControl, FormHelperText,
    TextField, Tooltip, CircularProgress
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import generateAudioStore from '../../Mobx/GenerateAudioStore';
import audioSettingsStore from '../../Mobx/AudioSettingsStore';
import {
    FALLBACK_INWORLD_VOICES, INWORLD_DELIVERY_MODES, INWORLD_NIQQUD_MODES, INWORLD_SPEAKING_RATE, LANGUAGE_KEYS,
    LANGUAGE_INFO, PROVIDER_GEMINI_API, PROVIDER_INWORLD, TEST_LANGUAGES, VOICE_INFO, providerOfModel, voiceLabel
} from '../../Mobx/AudioCatalog';

/** The providers of the test form: the value of the select, and the provider of the models that it shows */
const PROVIDER_OPTIONS = [
    { value: PROVIDER_GEMINI_API, label: 'Google (Gemini)' },
    { value: PROVIDER_INWORLD, label: 'Inworld' },
];

/** The key of the database (he, en_US...) of a language code of the test form (he-IL, en-US...) */
const languageKeyOfCode = (code) => LANGUAGE_KEYS.find((key) => LANGUAGE_INFO[key].testLanguage === code);

/**
 * A test of the text to speech: the text, the language, the voice and the model are chosen here (and the prompt for Gemini,
 * the pace and the delivery for Inworld), the audio file is created (nothing is stored on the server) and it is played in the
 * "Audio Generated" panel. The models and the voices are the ones that the server supports (GET /api/ai/settings/audio).
 */
const GenerateAudio = observer(() => {
    const store = generateAudioStore;
    const { models, allModels, voices, effective, capabilities, inworld } = audioSettingsStore;
    const selectedModel = allModels.find((model) => model.id === store.model);
    // Inworld can be used only when the server says that it has the key (a server that is older than this screen does not know Inworld at all)
    const inworldKeyMissing = capabilities?.inworldApiKey !== true;
    const geminiKeyMissing = capabilities?.geminiApiKey === false;
    const modelsOfProvider = allModels.filter((model) => providerOfModel(model) === providerOfModel(selectedModel));

    // The voices of Inworld that are saved for the language of the form (female first), as options of the voice field
    const languageKey = languageKeyOfCode(store.language);
    const inworldLanguages = inworld?.effective?.languages || FALLBACK_INWORLD_VOICES;
    const voicesOfLanguage = inworldLanguages[languageKey] || {};
    const inworldVoices = [
        ...(voicesOfLanguage.female || []).map((voice) => ({ voice, group: 'female' })),
        ...(voicesOfLanguage.male || []).map((voice) => ({ voice, group: 'male' })),
    ];
    const defaultInworldVoice = (voicesOfLanguage.female || voicesOfLanguage.male || [])[0];

    // The lists of the server and the automatic settings (once per visit of the page)
    useEffect(() => {
        audioSettingsStore.load();
    }, []);

    // The prompt starts from the automatic settings, unless the user already typed there
    useEffect(() => {
        store.applyDefaults(effective);
    }, [effective, store]);

    // The pace, the delivery and the niqqud of Inworld start from its settings, unless the user changed them
    useEffect(() => {
        store.applyInworldDefaults(inworld?.effective);
    }, [inworld, store]);

    // A model that the server does not know (a server that is older than this screen) is replaced by the first one that it knows
    useEffect(() => {
        if (!allModels.some((model) => model.id === store.model)) {
            store.setModel(models[0].id);
        }
    }, [allModels, models, store]);

    const unavailableReason = (model) => {
        if (providerOfModel(model) === PROVIDER_INWORLD) return inworldKeyMissing ? 'Inworld is not set up on the server (no API key)' : '';
        return geminiKeyMissing ? 'no Gemini API key on the server' : '';
    };

    /** Changing the provider shows its first model that can be used */
    const changeProvider = (provider) => {
        const candidates = allModels.filter((model) => providerOfModel(model) === provider);
        const usable = candidates.find((model) => !unavailableReason(model)) || candidates[0];
        if (usable) store.setModel(usable.id);
    };

    const isInworld = store.isInworld;

    return (
        <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom> {isInworld ? 'Inworld Text To Speech' : 'Gemini Text To Speech'} </Typography>
            <FormControl fullWidth margin="normal" size="small">
                <InputLabel id="provider-label">Provider</InputLabel>
                <Select
                    labelId="provider-label"
                    id="provider"
                    label="Provider"
                    value={isInworld ? PROVIDER_INWORLD : PROVIDER_GEMINI_API}
                    onChange={(e) => changeProvider(e.target.value)}
                >
                    {PROVIDER_OPTIONS.map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            disabled={option.value === PROVIDER_INWORLD && inworldKeyMissing}
                        >
                            {option.label}{option.value === PROVIDER_INWORLD && inworldKeyMissing ? ' - not set up on the server (no API key)' : ''}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                id="text"
                value={store.text}
                margin="normal"
                type="text"
                fullWidth
                multiline
                rows={4}
                label="text"
                size="small"
                inputProps={{ dir: 'auto' }}
                error={Boolean(store.textError)}
                helperText={store.textError
                    ? <bdi>{store.textError}</bdi>
                    : (isInworld ? <bdi>Spoken as in the dictionary: notes in parentheses, flags and quote marks are not read</bdi> : undefined)}
                onChange={(e) => store.setText(e.target.value)}
            />
            {!isInworld && (
                <TextField
                    label="Prompt"
                    type="text"
                    value={store.prompt}
                    onChange={(e) => store.setPrompt(e.target.value)}
                    margin="normal"
                    fullWidth
                    size="small"
                    inputProps={{ dir: 'auto' }}
                    helperText={<bdi>Style instructions for the speaker (tone, pace, accent). For the pace write, for example: Speak slowly</bdi>}
                />
            )}
            <FormControl fullWidth margin="normal" size="small">
                <InputLabel id="language-label">Language</InputLabel>
                <Select
                    labelId="language-label"
                    id="language"
                    value={store.language}
                    label="Language"
                    onChange={(e) => {
                        store.setLanguage(e.target.value);
                        // a voice of Inworld belongs to the language that was chosen when it was picked
                        if (isInworld) store.setInworldName('');
                    }}
                >
                    {TEST_LANGUAGES.map((language) => (
                        <MenuItem key={language.code} value={language.code}>{language.label}</MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                    <bdi>
                        {isInworld
                            ? 'Inworld needs the language: it also decides the default voice.'
                            : 'The model detects the language from the text. This choice only sets the English accent.'}
                    </bdi>
                </FormHelperText>
            </FormControl>
            {!isInworld ? (
                <FormControl fullWidth margin="normal" size="small">
                    <InputLabel id="name-label">Voice Name</InputLabel>
                    <Select
                        labelId="name-label"
                        id="name"
                        label="Voice Name"
                        value={voices.includes(store.name) ? store.name : ''}
                        onChange={(e) => store.setName(e.target.value)}
                    >
                        {voices.map((voice) => (
                            <MenuItem key={voice} value={voice}>
                                <Tooltip title={VOICE_INFO[voice]?.description || ''} placement="left" describeChild>
                                    <span>{voiceLabel(voice)}</span>
                                </Tooltip>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : (
                <Autocomplete
                    freeSolo
                    size="small"
                    options={inworldVoices}
                    groupBy={(option) => option.group}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.voice)}
                    inputValue={store.inworldName}
                    onInputChange={(event, value) => store.setInworldName(value)}
                    sx={{ mt: 2, mb: 1 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Voice"
                            error={Boolean(store.inworldNameError)}
                            helperText={<bdi>{store.inworldNameError
                                || (defaultInworldVoice
                                    ? `Empty: the default voice of the language (${defaultInworldVoice}). Another voice id of the account can be typed.`
                                    : 'Another voice id of the account can be typed.')}</bdi>}
                        />
                    )}
                />
            )}
            <FormControl fullWidth margin="normal" size="small">
                <InputLabel id="model-label">Model</InputLabel>
                <Select
                    labelId="model-label"
                    id="model"
                    label="Model"
                    value={selectedModel ? selectedModel.id : ''}
                    onChange={(e) => store.setModel(e.target.value)}
                >
                    {modelsOfProvider.map((model) => (
                        <MenuItem
                            key={model.id}
                            value={model.id}
                            disabled={Boolean(unavailableReason(model))}
                        >
                            {model.label}
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                    <bdi>
                        {isInworld
                            ? `Inworld - the audio is an MP3 file${selectedModel?.priceUsdPerMillionChars ? `, ${selectedModel.priceUsdPerMillionChars}$ per million characters` : ''}`
                            : 'Gemini API - the audio is a WAV file'}
                    </bdi>
                </FormHelperText>
            </FormControl>
            {isInworld && (
                <>
                    <TextField
                        label="Speaking rate"
                        type="number"
                        value={store.speakingRate}
                        onChange={(e) => store.setSpeakingRate(e.target.value)}
                        margin="normal"
                        fullWidth
                        size="small"
                        inputProps={{ min: INWORLD_SPEAKING_RATE.min, max: INWORLD_SPEAKING_RATE.max, step: 0.05 }}
                        error={Boolean(store.speakingRateError)}
                        helperText={<bdi>{store.speakingRateError || `${INWORLD_SPEAKING_RATE.min} (slow) to ${INWORLD_SPEAKING_RATE.max} (fast), 1 is normal`}</bdi>}
                    />
                    {store.supportsDeliveryMode && (
                        <FormControl fullWidth margin="normal" size="small">
                            <InputLabel id="delivery-label">Delivery mode</InputLabel>
                            <Select
                                labelId="delivery-label"
                                id="delivery"
                                label="Delivery mode"
                                value={store.deliveryMode}
                                onChange={(e) => store.setDeliveryMode(e.target.value)}
                            >
                                {INWORLD_DELIVERY_MODES.map((mode) => (
                                    <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText><bdi>STABLE is the most consistent, CREATIVE the most expressive</bdi></FormHelperText>
                        </FormControl>
                    )}
                    {store.isHebrew && (
                        <FormControl fullWidth margin="normal" size="small">
                            <InputLabel id="niqqud-label">Niqqud</InputLabel>
                            <Select
                                labelId="niqqud-label"
                                id="niqqud"
                                label="Niqqud"
                                value={store.niqqud}
                                onChange={(e) => store.setNiqqud(e.target.value)}
                            >
                                {INWORLD_NIQQUD_MODES.map((mode) => (
                                    <MenuItem key={mode} value={mode}>{mode === 'keep' ? 'keep - the voice reads the vowel points' : 'strip - remove the vowel points'}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </>
            )}

            {!store.loading ? (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={store.generateAudio}
                    disabled={!store.canGenerate}
                    fullWidth
                >
                    Generate
                </Button>
            ) : (
                <Button variant="contained" color="secondary" fullWidth disabled startIcon={<CircularProgress size={16} color="inherit" />}>
                    loading...
                </Button>
            )}
        </Box>
    );
});

export default GenerateAudio;
